/**
 * jump-graph.ts — wire each system's JumpPoints into a navigable hyperlane net
 * (GALAXY §3.5.1).
 *
 * After scenario.ts has placed the systems and their 3–6 outer-ring JPs, this
 * module pairs JPs across systems so every system is reachable from every
 * other and a few extra cross-links exist for routing variety.
 *
 * Strategy:
 *   1. Build a connected graph using a Kruskal-style minimum spanning tree
 *      keyed on Euclidean distance between systems. This guarantees every
 *      system has at least one link, and the network forms a single component.
 *   2. Add a small number of extra edges (the next-shortest candidates after
 *      the MST) to give the player more than one route between any two
 *      systems. Capped at ~30% of MST edge count.
 *   3. For each chosen system pair (A, B), reserve one previously-unused JP
 *      from each side. We pick the JP whose outer-ring angle best points
 *      toward the other system so links look natural on the tactical map.
 *      Mark those two JPs as used so they aren't reassigned later.
 *
 * Determinism: the only randomness used is in stable tie-breaking via
 * `state.rng`. Re-running with the same seed produces the same hyperlane
 * topology.
 *
 * Note on isolated systems: if a system runs out of free JPs (it has fewer
 * JPs than its desired degree) the edge is skipped. The MST step is built
 * first so connectivity is preserved; only the optional "extra" edges drop.
 */

import { nextFloat } from '../state/rng';
import type { GameState, JumpPoint, StarSystem, SystemId } from '../state/types';

/** Fraction of MST edges to add as extra cross-links for route variety. */
const EXTRA_EDGE_FRACTION = 0.3;

interface CandidateEdge {
  a: SystemId;
  b: SystemId;
  dist: number;
  /** RNG-drawn jitter so equal-distance ties resolve deterministically. */
  jitter: number;
}

/**
 * Wire jump-point links across `state`. Idempotent — calling on an already-
 * wired graph is a no-op because every JP is already linked.
 */
export function wireJumpGraph(state: GameState): void {
  const systems = [...state.systems.values()];
  if (systems.length < 2) return;

  // Per-system list of currently free JPs (mutated as we reserve them).
  const free = new Map<SystemId, JumpPoint[]>();
  for (const sys of systems) {
    const jps = sys.jumpPointIds
      .map((id) => state.jumpPoints.get(id))
      .filter((j): j is JumpPoint => !!j && j.linkedSystemId === null);
    free.set(sys.id, jps);
  }

  // Enumerate every system-pair as a candidate edge sorted by distance.
  const candidates: CandidateEdge[] = [];
  for (let i = 0; i < systems.length; i++) {
    for (let j = i + 1; j < systems.length; j++) {
      const a = systems[i]!;
      const b = systems[j]!;
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      candidates.push({
        a: a.id,
        b: b.id,
        dist: Math.hypot(dx, dy),
        jitter: nextFloat(state.rng) * 1e-6,
      });
    }
  }
  candidates.sort((p, q) => p.dist + p.jitter - (q.dist + q.jitter));

  // Kruskal MST with Union-Find.
  const parent = new Map<SystemId, SystemId>();
  for (const sys of systems) parent.set(sys.id, sys.id);
  const find = (x: SystemId): SystemId => {
    let r = x;
    while (parent.get(r)! !== r) r = parent.get(r)!;
    // Path compression.
    let cur = x;
    while (parent.get(cur)! !== r) {
      const next = parent.get(cur)!;
      parent.set(cur, r);
      cur = next;
    }
    return r;
  };
  const union = (x: SystemId, y: SystemId): boolean => {
    const rx = find(x);
    const ry = find(y);
    if (rx === ry) return false;
    parent.set(rx, ry);
    return true;
  };

  const mstEdges: CandidateEdge[] = [];
  const usedEdgeIdx = new Set<number>();
  for (let idx = 0; idx < candidates.length && mstEdges.length < systems.length - 1; idx++) {
    const e = candidates[idx]!;
    if (union(e.a, e.b)) {
      mstEdges.push(e);
      usedEdgeIdx.add(idx);
    }
  }

  // Apply MST edges first — preserves connectivity even if some systems are
  // JP-poor and later "extra" edges have to drop.
  for (const e of mstEdges) tryLinkEdge(state, e, free);

  // Optional cross-links for route variety.
  const extraTarget = Math.floor(mstEdges.length * EXTRA_EDGE_FRACTION);
  let extraAdded = 0;
  for (let idx = 0; idx < candidates.length && extraAdded < extraTarget; idx++) {
    if (usedEdgeIdx.has(idx)) continue;
    const e = candidates[idx]!;
    if (tryLinkEdge(state, e, free)) extraAdded++;
  }
}

/**
 * Try to wire a single edge by reserving the best-aligned free JP from each
 * end. Returns false if either endpoint has no free JPs left.
 */
function tryLinkEdge(
  state: GameState,
  edge: CandidateEdge,
  free: Map<SystemId, JumpPoint[]>,
): boolean {
  const sysA = state.systems.get(edge.a);
  const sysB = state.systems.get(edge.b);
  if (!sysA || !sysB) return false;

  const freeA = free.get(sysA.id);
  const freeB = free.get(sysB.id);
  if (!freeA || !freeB || freeA.length === 0 || freeB.length === 0) return false;

  const jpA = pickBestAlignedJp(freeA, sysA, sysB);
  const jpB = pickBestAlignedJp(freeB, sysB, sysA);
  if (!jpA || !jpB) return false;

  // Wire bidirectionally.
  jpA.linkedSystemId = sysB.id;
  jpA.linkedJumpPointId = jpB.id;
  jpB.linkedSystemId = sysA.id;
  jpB.linkedJumpPointId = jpA.id;

  // Remove from the free list.
  removeInPlace(freeA, jpA);
  removeInPlace(freeB, jpB);
  return true;
}

/**
 * Pick the free JP in `host` whose outer-ring direction (relative to the
 * star at (0.5, 0.5)) best matches the heading from host system to target
 * system. Returns null if no free JPs.
 */
function pickBestAlignedJp(
  freeJps: JumpPoint[],
  host: StarSystem,
  target: StarSystem,
): JumpPoint | null {
  if (freeJps.length === 0) return null;
  const hx = target.x - host.x;
  const hy = target.y - host.y;
  // Normalise heading; if the two systems sit on top of each other (shouldn't
  // happen in scenario.ts but cheap to guard), fall back to the first JP.
  const hLen = Math.hypot(hx, hy);
  if (hLen < 1e-9) return freeJps[0]!;
  const hxn = hx / hLen;
  const hyn = hy / hLen;

  let best: { jp: JumpPoint; dot: number } | null = null;
  for (const jp of freeJps) {
    const jx = jp.x - 0.5;
    const jy = jp.y - 0.5;
    const jLen = Math.hypot(jx, jy);
    if (jLen < 1e-9) continue;
    const dot = (jx / jLen) * hxn + (jy / jLen) * hyn;
    if (!best || dot > best.dot) best = { jp, dot };
  }
  return best?.jp ?? freeJps[0]!;
}

function removeInPlace<T>(arr: T[], item: T): void {
  const i = arr.indexOf(item);
  if (i >= 0) arr.splice(i, 1);
}
